import { AppLayout } from "components/AppLayout/AppLayout";
import { QuickNavModal } from "components/QuickNavModal/QuickNavModal";
import { SALES_TAX_QUICK_NAV_PAGES } from "constants/quickNav/salesTax";
import GroupSelect from "components/dashboard/sideBar/groupSelect";
import Loader from "components/DesignSystem/Loader/loader";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { GroupSelectorWithDefaultGroup } from "components/GroupSelector/GroupSelectorWithDefaultGroup";
import { Calculator } from "components/icons/LeftNav/SalesTax/Calculator";
import { Fillings as FillingsIcon } from "components/icons/LeftNav/SalesTax/Fillings";
import { NexusTracker } from "components/icons/LeftNav/SalesTax/NexusTracker";
import { Registration } from "components/icons/LeftNav/SalesTax/Registration";
import { InkleSalesTaxLogo } from "components/icons/Logos/InkleSalesTaxLogo";
import { InkleSalesTaxSymbol } from "components/icons/Logos/InkleSalesTaxSymbol";
import { RoutesAccrossApps } from "components/RoutesAcrossApps/RoutesAcrossApps";
import { SalesTaxServices } from "components/SalesTaxService/SalesTaxService";
import { LeftBar, LeftBarItem, LeftBarLogo } from "components/Sidebar/LeftBar";
import { SidebarStickyGroup } from "components/Sidebar/SidebarStickyGroup";
import { TopBar } from "components/TopBar/TopBar";
import { FOREIGN_CA } from "constants/onBoarding";
import { useCurrentAppContext } from "contexts/CurrentAppContext";
import { useAuth } from "hooks/useAuth";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { TaxCalculator } from "pages/SalesTax/Calculator/TaxCalculator";
import { Filings } from "pages/SalesTax/Filings/Filing";
import { SalesTaxRegistrations } from "pages/SalesTax/SalesTaxRegistrations/SalesTaxRegistrations";
import { useEffect, useState } from "react";
import { Switch, useRouteMatch } from "react-router-dom";
import { RestrictedRoute } from "RestrictedRoute";
import { DashboardBanner } from "signInSignUp/DashboardBanner";
import "static/styles/containers/dashboard.css";
import { SalesNexusTracker } from "./SalesNexusTracker";

export const Navbar = () => {
  const { type } = useAuth();

  const { link } = useConstructInternalLink();

  return (
    <div className="t-h-full t-flex t-flex-col t-justify-between">
      <div>
        {type === FOREIGN_CA && (
          <div className="t-m-2">
            <GroupSelect />
          </div>
        )}
        <LeftBarItem exact to={link("/")} icon={NexusTracker}>
          Nexus Tracker
        </LeftBarItem>
        <LeftBarItem to={link("/state-filings")} icon={FillingsIcon}>
          Filings
        </LeftBarItem>
        <LeftBarItem to={link("/registrations")} icon={Registration}>
          Registrations
        </LeftBarItem>
        <LeftBarItem to={link("/tax-calculator")} icon={Calculator}>
          Sales Tax Calculator
        </LeftBarItem>

        {/* <LeftBarItem to={link("/services")} icon={ServicesIcon}>
          Services
        </LeftBarItem> */}
      </div>
    </div>
  );
};

const SalesTaxEntitySwitcher = ({
  isCustomer,
  isCpa,
  isForeignCA,
  onGroupLoading,
}: {
  isCustomer?: boolean;
  isCpa?: boolean;
  isForeignCA?: boolean;
  onGroupLoading: (loading: boolean) => void;
}) => {
  const handleGroupChange = ({ isLoading }: { isLoading: boolean }) =>
    onGroupLoading(isLoading);

  return (
    <div className="t-flex t-flex-col t-gap-2">
      {(isCpa || isForeignCA) && (
        <GroupSelectorWithDefaultGroup
          size="small"
          onGroupChange={handleGroupChange}
        />
      )}
      <EntitySelector
        left={0}
        size="small"
        disableNonUsEntities
        showGoToEntity={isCustomer}
      />
    </div>
  );
};

export const SalesTaxDashboard = () => {
  const { path } = useRouteMatch();
  const { dispatch: currentAppDispatch } = useCurrentAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const { isCpa, isForeignCA, isCustomer } = useRoleBasedView();

  useEffect(() => {
    currentAppDispatch({
      type: "SET_CURRENT_APP",
      payload: { currentApp: isCustomer ? "Sales Tax" : "Sales Tax Admin" },
    });
  }, []);

  return (
    <AppLayout
      sidebar={
        <LeftBar
          logo={
            <LeftBarLogo
              logo={InkleSalesTaxLogo}
              symbol={InkleSalesTaxSymbol}
            />
          }
          subHeader={
            <SalesTaxEntitySwitcher
              isCustomer={isCustomer}
              isCpa={isCpa}
              isForeignCA={isForeignCA}
              onGroupLoading={setIsLoading}
            />
          }
          showCollapse
          stickyNav={<SidebarStickyGroup />}
        >
          <Navbar />
        </LeftBar>
      }
      topbar={
        <TopBar
          logo={
            <LeftBarLogo
              logo={InkleSalesTaxLogo}
              symbol={InkleSalesTaxSymbol}
            />
          }
          nav={<Navbar />}
          stickyNav={<SidebarStickyGroup />}
          subHeader={
            isCustomer && (
              <EntitySelector
                toolTipPlacement="bottom"
                right={0}
                disableNonUsEntities
                showGoToEntity
                size="small"
              />
            )
          }
        />
      }
      banner={<DashboardBanner />}
    >
      <QuickNavModal pages={SALES_TAX_QUICK_NAV_PAGES} />
      {isLoading ? (
        <div className="t-flex t-min-h-[60vh] t-w-full t-items-center t-justify-center">
          <Loader />
        </div>
      ) : (
        <Switch>
          {/* <RestrictedRoute exact path={`/sales-tax/services/addtask`}>
            <TemplateMenu
              to={`/sales-tax/services`}
              taskCategoryType="SALES_TAX"
            />
          </RestrictedRoute> */}
          {/* <RestrictedRoute path={`/sales-tax/services/:taskId`}>
            <FilingDetails isFromServices parentRoute="/sales-tax/services" />
          </RestrictedRoute> */}
          <RestrictedRoute path={`${path}/state-filings`} component={Filings} />
          <RestrictedRoute path={`${path}/services/`}>
            <SalesTaxServices />
          </RestrictedRoute>
          <RestrictedRoute path={`${path}/tax-calculator/`}>
            <TaxCalculator />
          </RestrictedRoute>

          <RestrictedRoute
            exact
            path={`${path}/registrations/`}
            component={SalesTaxRegistrations}
          />

          <RestrictedRoute exact path={path} component={SalesNexusTracker} />

          <RoutesAccrossApps />
        </Switch>
      )}
    </AppLayout>
  );
};
